// FT-089: адаптер смен для чистых gantt-утилит (assignLanes ждёт booking-shaped
// {id, _start, _end} с moment'ами — REQ-07). Смена: date "YYYY-MM-DD" + минуты.
import moment from "moment";

export const MINUTES_PER_DAY = 1440;

export const CATEGORY_COLORS = {
  work: "#4caf50",
  duty: "#e2850a",
  duty_night: "#673ab7",
  vacation: "#1976d2",
  vacation_no_salary: "#d2c619",
  sick: "#b7b8b4",
};

export const WORKING_CATEGORIES = ["work", "duty", "duty_night"];
export const ABSENCE_CATEGORIES = ["vacation", "vacation_no_salary", "sick"];

// Ленивая догрузка (REQ-07): расширение окна к краю на EXPAND_STEP_DAYS.
// Возвращает новое окно + диапазон, который нужно ДОГРУЗИТЬ (без перезагрузки всего).
export const EXPAND_STEP_DAYS = 30;

export function expandRange(edge, viewStart, viewEnd, stepDays = EXPAND_STEP_DAYS) {
  if (edge === "left") {
    const newStart = viewStart.clone().subtract(stepDays, "days");
    return {
      viewStart: newStart,
      viewEnd: viewEnd.clone(),
      fetchFrom: newStart.clone(),
      fetchTo: viewStart.clone().subtract(1, "day"),
    };
  }
  const newEnd = viewEnd.clone().add(stepDays, "days");
  return {
    viewStart: viewStart.clone(),
    viewEnd: newEnd,
    fetchFrom: viewEnd.clone().add(1, "day"),
    fetchTo: newEnd.clone(),
  };
}

// Merge догруженных смен с dedup по id (пересечения диапазонов безопасны)
export function mergeShifts(existing, incoming) {
  const seen = new Set(existing.map((s) => s.id));
  return existing.concat(incoming.filter((s) => !seen.has(s.id)));
}

const CATEGORY_ORDER = ["work", "duty", "duty_night", "vacation", "vacation_no_salary", "sick"];

// ── DST-безопасная геометрия (баг USER: Португалия, переход 29.03 ломал
// dateToPixel/лейны — миллисекундные сутки бывают 23/25 часов) ──
// Календарные дни считаем через Date.UTC от компонент даты: ровно N*86400000.
export function calendarDaysBetween(fromDate, toDate) {
  const [fy, fm, fd] = String(fromDate).slice(0, 10).split("-").map(Number);
  const [ty, tm, td] = String(toDate).slice(0, 10).split("-").map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000);
}

// Абсолютный интервал смены в МИНУТАХ от полуночи viewStart (зерко серверной математики)
export function shiftMinuteInterval(shift, viewStartDate) {
  const dayOffset = calendarDaysBetween(viewStartDate, shift.date) * MINUTES_PER_DAY;
  const start = dayOffset + (isAllDay(shift) ? 0 : shift.start_minute);
  const duration = isAllDay(shift) ? MINUTES_PER_DAY : shift.duration_minutes;
  return [ start, start + duration ];
}

// Пиксельная геометрия бара: минуты → доли ячейки
export function shiftBarMetrics(shift, viewStartDate, cellWidth) {
  const [ start, end ] = shiftMinuteInterval(shift, viewStartDate);
  return {
    left: (start / MINUTES_PER_DAY) * cellWidth,
    width: Math.max(2, ((end - start) / MINUTES_PER_DAY) * cellWidth),
  };
}

// Построение lane-элементов одного сотрудника (REQ-07 v2, split-день):
// - целодневные одного дня образуют слайс-группу (доли ячейки, lane общий);
//   для расчёта дорожек группа участвует ОДНИМ представителем — иначе
//   несколько видов дня раздували бы строку;
// - почасовые конкурируют интервалами как раньше (ночной хвост vs целодневная
//   следующего дня даёт lane 1 — валидная пара).
export function buildLaneItems(shifts, assignLanesFn, viewStartDate) {
  const anchor = viewStartDate || (shifts[0] && shifts[0].date) || "2026-01-01";
  const items = shifts.map((shift) => {
    const [ startMin, endMin ] = shiftMinuteInterval(shift, anchor);
    return {
      id: shift.id,
      // booking-shaped вход assignLanes: valueOf() в МИНУТАХ календарной шкалы —
      // DST-независимо (moment-миллисекунды давали 23/25-часовые сутки)
      _start: { valueOf: () => startMin },
      _end: { valueOf: () => endMin },
      shift,
    };
  });
  const allDayGroups = {};
  const laneInput = [];

  items.forEach((item) => {
    if (!isAllDay(item.shift)) {
      laneInput.push(item);
      return;
    }
    const key = item.shift.date;
    if (!allDayGroups[key]) {
      allDayGroups[key] = { members: [], repr: item };
      laneInput.push(item); // представитель группы
    }
    allDayGroups[key].members.push(item);
  });

  const { lanes } = assignLanesFn(laneInput);

  Object.values(allDayGroups).forEach((group) => {
    group.members.sort(
      (a, b) => CATEGORY_ORDER.indexOf(a.shift.category) - CATEGORY_ORDER.indexOf(b.shift.category) || a.id - b.id
    );
    group.members.forEach((member, index) => {
      member.lane = lanes[group.repr.id] || 0;
      member.sliceIndex = index;
      member.sliceCount = group.members.length;
    });
  });
  laneInput.forEach((item) => {
    if (isAllDay(item.shift)) return;
    item.lane = lanes[item.id] || 0;
    item.sliceIndex = 0;
    item.sliceCount = 1;
  });

  return items;
}

// Ключи переводов категорий в существующей секции schedules.* (sick → sick_day)
export function categoryLocaleKey(category) {
  return category === "sick" ? "schedules.sick_day" : `schedules.${category}`;
}

// Имя шаблона для показа: сид-шаблоны (system_default) локализуются по
// категории под язык зрителя (имя в БД — в локали компании); кастомные —
// как назвал юзер.
export function templateDisplayName(template, translate) {
  if (!template) return "";
  if (template.system_default && CATEGORY_COLORS[template.category] !== undefined) {
    return translate(categoryLocaleKey(template.category));
  }
  return template.name;
}

export function isAllDay(shift) {
  return shift.start_minute === null || shift.start_minute === undefined;
}

// Смена → интервальный объект для assignLanes/dateToPixel.
// Целодневная = 00:00..24:00 якорного дня; ночная уходит хвостом в следующий день.
export function toLaneItem(shift) {
  const dayStart = moment(shift.date, "YYYY-MM-DD").startOf("day");
  if (isAllDay(shift)) {
    return {
      id: shift.id,
      _start: dayStart.clone(),
      _end: dayStart.clone().add(MINUTES_PER_DAY, "minutes"),
      shift,
    };
  }
  const start = dayStart.clone().add(shift.start_minute, "minutes");
  return {
    id: shift.id,
    _start: start,
    _end: start.clone().add(shift.duration_minutes, "minutes"),
    shift,
  };
}

// 540 → "09:00"; хвост за полночь показываем по модулю суток (21:00–09:00)
export function minutesLabel(totalMinutes) {
  const h = Math.floor(totalMinutes / 60) % 24;
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function hoursLabel(shift) {
  if (isAllDay(shift)) return "";
  return `${minutesLabel(shift.start_minute)}–${minutesLabel(shift.start_minute + shift.duration_minutes)}`;
}

// "HH:mm" → минуты от полуночи (null при мусоре)
export function parseTimeToMinutes(value) {
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(String(value || "").trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}
